/**
 * PM Command Center
 *
 * Main hub for PM workflow management.
 * Features:
 * - Email Intake Dashboard (production)
 * - Work Order Queue (coming soon)
 * - Prototype options for UX testing
 */

import { useState } from "react";
import EmailIntakeDashboard from "./EmailIntakeDashboard";
import PMWorkOrderDemo from "./PMWorkOrderDemo";
import WorkOrderQueue from "./WorkOrderQueue";
import PMCenterOptionC from "./PMCenterOptionC";
import PMCenterOptionD from "./PMCenterOptionD";
import PMCenterOptionE from "./PMCenterOptionE";
import { PMInterface } from "./pm";
import { isFeatureEnabled, FEATURES } from "../config/features";

export default function PMCommandCenter({ onBackToApp, userEmail }) {
  // activeView: "home" | "intake" | "review" | "queue" | "C" | "D" | "E" | "pipeline"
  // Default to Pipeline - the new 4-Tab Interface
  const [activeView, setActiveView] = useState("pipeline");
  // Track which intake to focus on when navigating to review
  const [focusIntakeId, setFocusIntakeId] = useState(null);

  // Navigate to work order review, optionally with a specific intake to focus
  const handleNavigateToReview = (intakeId = null) => {
    setFocusIntakeId(intakeId);
    setActiveView("review");
  };

  // Work Order Review (using the full Demo/Live component)
  if (activeView === "review") {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col">
        <header className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 px-6 py-3 flex items-center justify-between text-gray-900">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setActiveView("intake")}
              className="text-gray-900/80 hover:text-gray-900"
            >
              ← Back to Intake
            </button>
            <h1 className="text-lg font-semibold">Work Order Review</h1>
          </div>
          <button
            onClick={onBackToApp}
            className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-md text-sm"
          >
            Exit PM Center
          </button>
        </header>
        <div className="flex-1 overflow-y-auto">
          <PMWorkOrderDemo focusIntakeId={focusIntakeId} />
        </div>
      </div>
    );
  }

  // Email Intake Dashboard
  if (activeView === "intake") {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col">
        <header className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 px-6 py-3 flex items-center justify-between text-gray-900">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setActiveView("home")}
              className="text-gray-900/80 hover:text-gray-900"
            >
              ← Back
            </button>
            <h1 className="text-lg font-semibold">Email Intake</h1>
          </div>
          <button
            onClick={onBackToApp}
            className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-md text-sm"
          >
            Exit PM Center
          </button>
        </header>
        <div className="flex-1">
          <EmailIntakeDashboard onNavigateToReview={handleNavigateToReview} />
        </div>
      </div>
    );
  }

  // Work Order Queue
  if (activeView === "queue") {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col">
        <header className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 px-6 py-3 flex items-center justify-between text-gray-900">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setActiveView("home")}
              className="text-gray-900/80 hover:text-gray-900"
            >
              ← Back
            </button>
            <h1 className="text-lg font-semibold">Work Order Queue</h1>
          </div>
          <button
            onClick={onBackToApp}
            className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-md text-sm"
          >
            Exit PM Center
          </button>
        </header>
        <div className="flex-1 overflow-hidden">
          <WorkOrderQueue />
        </div>
      </div>
    );
  }

  // Prototype options
  if (activeView === "C") {
    return <PMCenterOptionC onBackToApp={() => setActiveView("home")} />;
  }

  if (activeView === "D") {
    return <PMCenterOptionD onBackToApp={() => setActiveView("home")} />;
  }

  if (activeView === "E") {
    // Option E is the legacy 3-Tab Workflow
    return <PMCenterOptionE onBackToApp={onBackToApp} />;
  }

  if (activeView === "pipeline") {
    // New Pipeline-First 4-Tab Interface
    return <PMInterface onBackToApp={onBackToApp} />;
  }

  // Home screen with production features and prototype options
  return (
    <div className="flex-1 overflow-auto bg-slate-100 text-gray-900 flex flex-col">

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-y-auto">
        <div className="max-w-6xl mx-auto">
          {/* Production Features Section */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6">PM Workflow</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Email Intake - Primary CTA */}
              <div
                onClick={() => setActiveView("intake")}
                className="bg-gradient-to-br from-green-50 to-emerald-100 border-2 border-green-300 rounded-2xl p-6 cursor-pointer hover:border-green-400 hover:shadow-lg hover:shadow-green-500/20 transition-all group"
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-3xl">📧</span>
                  <div>
                    <h3 className="text-xl font-bold">Email Intake</h3>
                    <span className="text-xs px-2 py-0.5 bg-green-500/30 text-green-600 rounded">LIVE</span>
                  </div>
                </div>
                <p className="text-gray-700 text-sm mb-4">
                  Review AI-extracted project kickoff emails. Approve to create work orders or request clarification.
                </p>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    Powered by OpenAI extraction
                  </div>
                  <button className="px-4 py-2 bg-green-600 hover:bg-green-500 rounded-lg font-medium transition-colors">
                    Open →
                  </button>
                </div>
              </div>

              {/* Work Order Queue */}
              <div
                onClick={() => setActiveView("queue")}
                className="bg-gradient-to-br from-blue-50 to-indigo-100 border-2 border-blue-300 rounded-2xl p-6 cursor-pointer hover:border-blue-400 hover:shadow-lg hover:shadow-blue-500/20 transition-all group"
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-3xl">📋</span>
                  <div>
                    <h3 className="text-xl font-bold">Work Order Queue</h3>
                    <span className="text-xs px-2 py-0.5 bg-blue-500/30 text-blue-600 rounded">LIVE</span>
                  </div>
                </div>
                <p className="text-gray-700 text-sm mb-4">
                  Prioritized queue of work orders. Track status, assign PMs, and manage production handoff.
                </p>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    Rush orders prioritized
                  </div>
                  <button className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg font-medium transition-colors">
                    Open →
                  </button>
                </div>
              </div>

              {/* Production Dashboard - Coming Soon */}
              <div className="bg-white border border-gray-200 rounded-2xl p-6 opacity-60">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-3xl">📊</span>
                  <div>
                    <h3 className="text-xl font-bold">Production Dashboard</h3>
                    <span className="text-xs px-2 py-0.5 bg-slate-200/30 text-gray-500 rounded">COMING SOON</span>
                  </div>
                </div>
                <p className="text-gray-500 text-sm mb-4">
                  Real-time view of production status. See what's in progress without walking the floor.
                </p>
                <div className="text-xs text-gray-500">
                  After work order integration
                </div>
              </div>
            </div>
          </div>

          {/* UX Prototypes Section - Only show if feature flag enabled */}
          {isFeatureEnabled(FEATURES.PM_CENTER_PROTOTYPES, userEmail) && (
          <div className="border-t border-gray-200 pt-8">
            <div className="flex items-center gap-3 mb-6">
              <h2 className="text-xl font-bold text-gray-500">UX Prototypes</h2>
              <span className="text-xs px-2 py-0.5 bg-purple-500/30 text-purple-600 rounded">MOCK DATA</span>
            </div>
            <p className="text-gray-500 text-sm mb-6">
              Test different UX approaches for the full PM workflow. These use mock data for evaluation.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Option C Card */}
            <div
              onClick={() => setActiveView("C")}
              className="bg-white border border-gray-200 rounded-2xl p-6 cursor-pointer hover:border-purple-500/50 hover:bg-white transition-all group"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="text-3xl">📥</span>
                <div>
                  <h3 className="text-xl font-bold">Option C</h3>
                  <p className="text-sm text-purple-600">Inbox + Focus</p>
                </div>
              </div>

              {/* Mini Preview */}
              <div className="bg-gray-300 rounded-lg p-3 mb-4 text-xs">
                <div className="flex gap-2 h-32">
                  <div className="w-24 bg-slate-100 rounded p-2">
                    <div className="text-gray-500 mb-1">Filters</div>
                    <div className="space-y-1">
                      <div className="h-2 bg-purple-600/50 rounded" />
                      <div className="h-2 bg-gray-200 rounded" />
                      <div className="h-2 bg-gray-200 rounded" />
                    </div>
                    <div className="mt-2 text-gray-500">List</div>
                    <div className="space-y-1 mt-1">
                      <div className="h-3 bg-gray-200 rounded" />
                      <div className="h-3 bg-gray-200 rounded" />
                      <div className="h-3 bg-gray-200 rounded" />
                    </div>
                  </div>
                  <div className="flex-1 bg-slate-100 rounded p-2">
                    <div className="text-gray-500 mb-1">Project Workspace</div>
                    <div className="flex gap-1 mb-2">
                      <div className="h-2 w-8 bg-purple-600/50 rounded" />
                      <div className="h-2 w-8 bg-gray-200 rounded" />
                      <div className="h-2 w-8 bg-gray-200 rounded" />
                    </div>
                    <div className="h-16 bg-gray-200/50 rounded" />
                  </div>
                </div>
              </div>

              <div className="space-y-2 text-sm text-gray-700 mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-green-400">✓</span>
                  <span>Left sidebar with smart filters</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-400">✓</span>
                  <span>Full project workspace on right</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-400">✓</span>
                  <span>Tabbed interface for project tasks</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-400">✓</span>
                  <span>Email/Slack-like familiar pattern</span>
                </div>
              </div>

              <button className="w-full py-3 bg-purple-600 hover:bg-purple-700 rounded-lg font-medium transition-colors group-hover:bg-purple-500">
                Test Option C →
              </button>
            </div>

            {/* Option D Card */}
            <div
              onClick={() => setActiveView("D")}
              className="bg-white border border-gray-200 rounded-2xl p-6 cursor-pointer hover:border-indigo-500/50 hover:bg-white transition-all group"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="text-3xl">🎛️</span>
                <div>
                  <h3 className="text-xl font-bold">Option D</h3>
                  <p className="text-sm text-indigo-400">Mission Control + Inbox</p>
                </div>
              </div>

              {/* Mini Preview */}
              <div className="bg-gray-300 rounded-lg p-3 mb-4 text-xs">
                <div className="flex flex-col gap-2 h-32">
                  {/* Mission Control Bar */}
                  <div className="flex gap-2">
                    <div className="px-2 py-1 bg-red-600/50 rounded text-center">
                      <div className="font-bold">2</div>
                      <div className="text-[8px]">Today</div>
                    </div>
                    <div className="px-2 py-1 bg-yellow-600/50 rounded text-center">
                      <div className="font-bold">4</div>
                      <div className="text-[8px]">Answers</div>
                    </div>
                    <div className="px-2 py-1 bg-blue-600/50 rounded text-center">
                      <div className="font-bold">3</div>
                      <div className="text-[8px]">Intake</div>
                    </div>
                    <div className="px-2 py-1 bg-green-600/50 rounded text-center">
                      <div className="font-bold">5</div>
                      <div className="text-[8px]">Prod</div>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-1">
                    <div className="w-20 bg-slate-100 rounded p-1">
                      <div className="space-y-1">
                        <div className="h-2 bg-gray-200 rounded" />
                        <div className="h-2 bg-gray-200 rounded" />
                        <div className="h-2 bg-indigo-600/50 rounded" />
                      </div>
                    </div>
                    <div className="flex-1 bg-slate-100 rounded p-1">
                      <div className="h-full bg-gray-200/50 rounded" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-2 text-sm text-gray-700 mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-green-400">✓</span>
                  <span>Mission Control bar with clickable badges</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-400">✓</span>
                  <span>At-a-glance metrics + quick filtering</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-400">✓</span>
                  <span>Same workspace as Option C</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-400">✓</span>
                  <span>Visual urgency indicators</span>
                </div>
              </div>

              <button className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 rounded-lg font-medium transition-colors group-hover:bg-indigo-500">
                Test Option D →
              </button>
            </div>
            </div>
          </div>
          )}

          {/* Option E Section - Always visible (LIVE) */}
          <div className="border-t border-gray-200 pt-8">
            <div className="flex items-center gap-3 mb-6">
              <h2 className="text-xl font-bold text-gray-700">PM Workflow</h2>
              <span className="text-xs px-2 py-0.5 bg-emerald-500 text-gray-900 rounded font-bold">LIVE</span>
            </div>

            {/* Option E Card - NOW LIVE */}
            <div
              onClick={() => setActiveView("E")}
              className="bg-white border-2 border-emerald-500/50 rounded-2xl p-6 cursor-pointer hover:border-emerald-400 hover:bg-white transition-all group ring-2 ring-emerald-500/20"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="text-3xl">📋</span>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-xl font-bold">Option E</h3>
                    <span className="text-[10px] px-1.5 py-0.5 bg-emerald-500 text-gray-900 rounded font-bold">LIVE DATA</span>
                  </div>
                  <p className="text-sm text-emerald-400">3-Tab Workflow</p>
                </div>
              </div>

              {/* Mini Preview */}
              <div className="bg-gray-300 rounded-lg p-3 mb-4 text-xs">
                <div className="flex flex-col gap-2 h-32">
                  {/* Tab Bar */}
                  <div className="flex gap-1">
                    <div className="px-2 py-1 bg-emerald-600/50 rounded text-[8px]">Quotes</div>
                    <div className="px-2 py-1 bg-gray-200 rounded text-[8px]">Work Orders</div>
                    <div className="px-2 py-1 bg-gray-200 rounded text-[8px]">Active</div>
                  </div>
                  {/* Content area */}
                  <div className="flex-1 flex gap-2">
                    <div className="flex-1 bg-slate-100 rounded p-2">
                      <div className="text-gray-500 mb-1 text-[8px]">New Kickoffs</div>
                      <div className="space-y-1">
                        <div className="h-2 bg-red-600/50 rounded" />
                        <div className="h-2 bg-yellow-600/50 rounded" />
                      </div>
                      <div className="text-gray-500 mt-2 mb-1 text-[8px]">Assigned</div>
                      <div className="space-y-1">
                        <div className="h-2 bg-gray-200 rounded" />
                        <div className="h-2 bg-gray-200 rounded" />
                      </div>
                    </div>
                    <div className="w-20 bg-slate-100 rounded p-2">
                      <div className="text-gray-500 mb-1 text-[8px]">Actions</div>
                      <div className="h-3 bg-emerald-600/30 rounded mb-1" />
                      <div className="h-3 bg-gray-200 rounded" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-2 text-sm text-gray-700 mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-green-400">✓</span>
                  <span>Tab 1: Quotes + Kickoff queue</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-400">✓</span>
                  <span>Tab 2: Work order creation</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-400">✓</span>
                  <span>Tab 3: Active project management</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-400">✓</span>
                  <span>PM assignment + self-claim workflow</span>
                </div>
              </div>

              <button className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 rounded-lg font-medium transition-colors group-hover:bg-emerald-500">
                Open Option E →
              </button>
            </div>
          </div>

          {/* Comparison Notes - Only show when prototypes are visible */}
          {isFeatureEnabled(FEATURES.PM_CENTER_PROTOTYPES, userEmail) && (
          <div className="mt-8 bg-slate-200 border border-gray-200 rounded-xl p-6">
            <h3 className="font-semibold mb-3">Key Differences to Evaluate:</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <h4 className="text-purple-600 font-medium mb-1">Option C: Inbox + Focus</h4>
                <ul className="space-y-1 text-gray-500">
                  <li>• Vertical filter list in sidebar</li>
                  <li>• More traditional email-client layout</li>
                  <li>• Counts shown next to each filter</li>
                  <li>• Clean, minimal header</li>
                </ul>
              </div>
              <div>
                <h4 className="text-indigo-400 font-medium mb-1">Option D: Mission Control</h4>
                <ul className="space-y-1 text-gray-500">
                  <li>• Horizontal mission control badges</li>
                  <li>• Clickable badges filter the list</li>
                  <li>• Urgent items more visually prominent</li>
                  <li>• Dashboard-first approach</li>
                </ul>
              </div>
              <div>
                <h4 className="text-emerald-400 font-medium mb-1">Option E: 3-Tab Workflow</h4>
                <ul className="space-y-1 text-gray-500">
                  <li>• Structured workflow by stage</li>
                  <li>• PM assignment + self-claim</li>
                  <li>• Dedicated work order creation</li>
                  <li>• Matches your team structure</li>
                </ul>
              </div>
            </div>
          </div>
          )}
        </div>
      </main>
    </div>
  );
}
